import React, { useMemo } from 'react';
import { Box, Card, CardHeader, CardContent, Typography, CircularProgress, Stack } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useItemTooltip } from '@mui/x-charts';
import { usePerformanceReportingContext } from '../PerformanceReportingContext';
import useTop5Summaries from '../_data_sources/useTop5Summaries';
import { Top5Summary } from '../_types';
import { neutral, primary } from '_lex/_foundations/phxColors';

/**
 * Truncates text to approximately 100px width (roughly 12-15 characters)
 */
function truncateText(text: string, maxLength: number = 15): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

function CustomTooltipContent({ summary }: { summary: Top5Summary }) {
  const item = useItemTooltip();
  if (!item) return null;

  const dataIndex = item.identifier?.dataIndex;
  if (typeof dataIndex !== 'number' || dataIndex < 0 || dataIndex >= summary.data.length) {
    return null;
  }

  const dataPoint = summary.data[dataIndex];
  const valueText =
    item.formattedValue ?? (typeof item.value === 'number' ? item.value.toLocaleString() : String(item.value));

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        minWidth: 220,
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant='caption'>
          <b>{dataPoint.name}</b>
        </Typography>
        {dataPoint.description.map((line, index) => (
          <Typography
            key={index}
            variant='caption'
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {index === 0 && (
              <Box
                component='span'
                sx={{ width: 10, height: 10, bgcolor: item.color, borderRadius: '50%' }}
              />
            )}
            {line}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

function Top5SummaryChart({ summary }: { summary: Top5Summary }) {
  // Data is already sorted descending from the API
  const chartData = useMemo(() => {
    return summary.data.map((item, index) => {
      return {
        id: index,
        value: item.value,
        label: item.name, // Keep full label for tooltip
      };
    });
  }, [summary.data]);

  const labels = useMemo(() => {
    return chartData.map((d) => d.label);
  }, [chartData]);

  const maxValue = useMemo(() => {
    return Math.max(...chartData.map((d) => d.value), 0);
  }, [chartData]);

  if (chartData.length === 0) {
    return (
      <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}` }}>
        <CardHeader title={summary.name} />
        <CardContent>
          <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              sx={{
                color: neutral[500],
                fontSize: 14,
              }}
            >
              No data available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // For horizontal bar charts: y-axis should be 'band' (categories), x-axis should be linear (numeric)
  // Use valueFormatter to truncate axis labels, but keep full labels in data for tooltips
  const xAxisConfig = [{ min: 0, max: maxValue > 0 ? Math.ceil(maxValue * 1.1) : 10 }];
  const yAxisConfig = [
    {
      scaleType: 'band' as const,
      data: labels, // Full labels - used for tooltip
      valueFormatter: (value: string) => truncateText(value), // Truncate only the displayed axis label
    },
  ];

  return (
    <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}` }}>
      <CardHeader title={summary.name} />
      <CardContent>
        <Box
          sx={{
            width: '100%',
            height: 300,
            '& .MuiChartsAxis-root[data-axis-id="y-axis-0"] .MuiChartsAxis-tickLabel': {
              cursor: 'help',
            },
          }}
        >
          <BarChart
            layout='horizontal'
            xAxis={xAxisConfig}
            yAxis={yAxisConfig}
            series={[
              {
                data: chartData.map((d) => d.value),
                label: summary.name,
                color: primary[600],
                valueFormatter: (value, context) => {
                  const dataPoint = summary.data[context.dataIndex];
                  return dataPoint.description.join('\n');
                },
              },
            ]}
            height={300}
            margin={{ top: 10, bottom: 30, left: 120, right: 10 }}
            tooltip={{ trigger: 'item' }}
            slotProps={{
              legend: { hidden: true },
            }}
            slots={{
              itemContent: () => <CustomTooltipContent summary={summary} />,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export function Top5Summaries() {
  const { orgId, childOrgId, queryParams } = usePerformanceReportingContext();
  const { data: summaries, isLoading } = useTop5Summaries({
    orgId,
    childOrgId,
    assignedModule: queryParams.assignedModule,
    startDate: queryParams.startDate,
    endDate: queryParams.endDate,
  });

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!summaries || summaries.length === 0) {
    return null;
  }

  return (
    <Stack
      direction='row'
      spacing={4}
      sx={{
        flexWrap: 'wrap',
        '& > *': {
          flex: '1 1 400px',
          minWidth: 400,
        },
      }}
    >
      {summaries.map((summary, index) => (
        <Top5SummaryChart
          key={`top5-summary-${summary.name}-${index}`}
          summary={summary}
        />
      ))}
    </Stack>
  );
}

export default Top5Summaries;
